/**
 * caseShareState.js — a case with a live share code IS SHARED, whatever its
 * database status says.
 *
 * ★★★ THE DEFECT THIS EXISTS TO CLOSE (TF b37 #2). Dr. Rami, on a case he had
 *   just shared by code: "This case was shared by share code. So it's already
 *   shared but not claimed. Should read Shared."
 *
 *   Sharing by CODE does not move `cases.status`. The row stays
 *   `ready_to_share` and gains a `share_code` — and every status reader in the
 *   app looked only at the status:
 *
 *     CaseHome.jsx:579        if (currentCase.status === 'ready_to_share') return 'ready_to_share'
 *     utils/caseBadges.js:47  if (STATUS_BADGES[status]) ...
 *
 *   So the header pill, the Cases-list pill and the Home card all said "Ready to
 *   Share" about a case that had already been shared and was waiting for a
 *   doctor to open it. Two separate implementations of one fact, and the fact
 *   was wrong in both.
 *
 * ★★ WHY A THIRD FILE RATHER THAN A FIX IN EACH. Fixing both would have left two
 *   copies that a later change can drift apart again — which is how they came to
 *   disagree with the store in the first place. `check-case-share-state.mjs` has
 *   a source arm that fails if either caller grows its own copy of the rule.
 *
 * ★ WHAT "LIVE" MEANS, AND ITS ONE LIMIT, STATED. Revocation is handled: the API
 *   clears `share_code`, the store's `apiIsWithdrawn` path nulls it
 *   (stores/index.js:1129), and the case correctly reverts to Ready to Share.
 *   EXPIRY IS NOT. The agreement text promises a 7-day life, but no expiry
 *   timestamp reaches the case object — `expiresAt` exists only on the
 *   share-creation RESPONSE (stores/index.js:2260, :2278), never on the case
 *   row the list renders. So an expired-but-not-revoked code still reads as
 *   Shared here. That is a server/transform gap, not something this file can
 *   invent, and it is filed rather than papered over.
 */

/** The share code on a case, in any of the shapes the store emits. */
export function shareCodeOf(c) {
  if (!c) return null
  const raw = c.shareCode || c.share_code || null
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Has a doctor taken the case? Claiming is what ends the code-shared state. */
export function hasClaimingDoctor(c) {
  if (!c) return false
  return !!(c.doctorId || c.doctor_id || c.doctor || c.sentToDoctorName)
}

/**
 * Shared by code and nobody has picked it up yet.
 * ★ This is the state TF b37 #3 asks for its own copy — the patient is NOT
 *   stuck, so the dialogue must not read like a waiting room.
 */
export function isCodeSharedUnclaimed(c) {
  return !!shareCodeOf(c) && !hasClaimingDoctor(c)
}

/**
 * Genuinely ready to share: the patient finished Describe and has done nothing
 * with it — no code minted, no doctor. This is the ONLY state where the pill
 * should read "Ready to Share", and per TF b37 #2 the only one where it earns a
 * call to action.
 */
export function isTrulyReadyToShare(c) {
  if (!c) return false
  if (shareCodeOf(c)) return false
  if (hasClaimingDoctor(c)) return false
  const describeDone = !!(c.describeComplete || c.describe_complete || (c.describe && c.describe.completed))
  const status = c.status
  return (status === 'ready_to_share' || status === 'draft') && describeDone
}

/**
 * The one correction, applied to whatever status a caller derived.
 *
 * ★ It ONLY ever rewrites `ready_to_share` → `shared`. It cannot promote a
 *   claimed, scheduled, checked-in, in-progress or finalised case, and it cannot
 *   demote anything — a stale code on a case a doctor has since claimed must not
 *   drag it backwards. Deliberately narrow: this is a display correction, not a
 *   status machine, and the status machine lives on the server.
 */
export function resolveShareStatus(derivedStatus, c) {
  if (derivedStatus !== 'ready_to_share') return derivedStatus
  return shareCodeOf(c) ? 'shared' : 'ready_to_share'
}

/* ════════════════════════════════════════════════════════════════════════════
 * TEST 1.19 — WHAT THE "Your visit" CARD IS ALLOWED TO CLAIM ABOUT A DOCTOR.
 *
 * ★★★ THE DEFECT, MEASURED 2026-09-21. Two readers in CaseHome.jsx decided the
 *   whole question on the MERE EXISTENCE of a share code:
 *
 *     return code ? t('caseHome.awaitingDoctor') : t('caseHome.notSharedYet')
 *     if (drShareCode) return <Tag tone="warn">{t('caseHome.awaitingDoctor')}</Tag>
 *     drSub = drShareCode ? t('caseHome.awaitingAssignment') : ...
 *
 *   Nothing there tests whether a physician was ever ADDRESSED. "Awaiting" is a
 *   claim that somebody was asked and has not answered; the condition above is
 *   true the instant a code is minted. So the card was false in two ordinary
 *   situations:
 *     · an `external` case, where no Orella physician will ever pick it up, and
 *     · a patient who generated a code and has not handed it to anybody yet.
 *   "Awaiting assignment" was worse again: it names a QUEUE and an ASSIGNER,
 *   and on the code path neither exists.
 *
 * ★★★ AND THE REASON STATE A IS AN INSTRUCTION RATHER THAN A STATUS — this is
 *   the whole point, not a stylistic preference. THE APP CANNOT KNOW WHETHER
 *   THE PATIENT HAS ALREADY READ THE CODE OUT LOUD. `cases.shared_at` is
 *   written only by the server's send-to-doctor route, never by minting a code,
 *   and `sent_to_doctor_at` has no server writer at all. So "nobody has used
 *   this code yet" IS NOT A FACT WE HOLD. An instruction ("give it to your
 *   doctor") is true whether or not he has; any status is false in one of the
 *   two branches. We say what the patient can DO, because that is the only
 *   thing on this path we actually know.
 *
 * ★★ THE FIELD THE BRIEFING GOT WRONG, AND IT WOULD HAVE MADE STATE B DEAD.
 *   `target_doctor_id` IS NOT ON THE CASE OBJECT. The store maps it at
 *   stores/index.js:1431 as
 *       sentToDoctor: c.target_doctor_id || c.doctor_id,
 *   and emits no `target_doctor_id` / `targetDoctorId` of its own (measured:
 *   the identifier appears in stores/index.js three times, at :1246, :1431 and
 *   :1436, all reading the RAW API ROW `c`, never the mapped case). A reader
 *   that tested `currentCase.target_doctor_id` would be permanently undefined,
 *   state B would never be reached, and every genuinely-addressed case would
 *   have fallen through to state A — silently deleting the one place where a
 *   waiting word is honest. Both spellings are still accepted below in case the
 *   transform ever grows them; `sentToDoctor` is the one that fires today.
 *   ★ `sentToDoctor` is a UNION of target and claiming doctor, which is exactly
 *   why hasAttendingDoctor() is tested FIRST: once a doctor has claimed, the
 *   union is no longer evidence of an unanswered address.
 * ════════════════════════════════════════════════════════════════════════════ */

/** The patient brought their own doctor; Orella is not arranging this visit. */
export function isExternalDoctorPath(c) {
  if (!c) return false
  return c.doctorSource === 'external' || c.doctor_source === 'external'
}

/**
 * The doctor record actually on the case, read down the path the case is on.
 * ★ The two paths keep the doctor in DIFFERENT fields, and reading the wrong
 *   one is how an external case reports "no doctor" while showing his name.
 */
export function attendingDoctorOf(c) {
  if (!c) return null
  return isExternalDoctorPath(c)
    ? (c.externalDoctor || c.external_doctor || null)
    : (c.doctor || null)
}

/** Is a doctor ON the case — claimed it, or named on it? */
export function hasAttendingDoctor(c) {
  if (!c) return false
  if (attendingDoctorOf(c)) return true
  // A claiming id only counts on the Orella path; an external case has no
  // Orella doctor row to point at.
  return !isExternalDoctorPath(c) && !!(c.doctorId || c.doctor_id)
}

/**
 * An Orella physician was ADDRESSED and has not claimed it yet. This — and
 * only this — is a state in which a waiting word is true: somebody specific
 * was asked, and the answer is outstanding.
 */
export function hasAddressedOrellaDoctor(c) {
  if (!c) return false
  if (isExternalDoctorPath(c)) return false
  return !!(c.targetDoctorId || c.target_doctor_id || c.sentToDoctor || c.sentToDoctorName)
}

/** The six states the card may be in. Exported so the gate cannot drift from it. */
export const DOCTOR_WAIT_STATES = Object.freeze([
  'with_doctor',
  'orella_pending',
  'code_ready',
  'external_code_ready',
  'external_no_doctor',
  'nothing_shared',
])

/**
 * ★ ORDER IS THE RULE, not an implementation detail.
 *   1. A doctor on the case ends every other question.
 *   2. An addressed Orella physician is state B — the only honest "awaiting".
 *   3. A code with nobody addressed is state A — an instruction, never a status.
 *   4. External with no code is state C — Orella awaits nothing; say so.
 */
export function resolveDoctorWaitState(c) {
  if (!c) return 'nothing_shared'
  if (hasAttendingDoctor(c)) return 'with_doctor'
  if (hasAddressedOrellaDoctor(c)) return 'orella_pending'
  const external = isExternalDoctorPath(c)
  if (shareCodeOf(c)) return external ? 'external_code_ready' : 'code_ready'
  if (external) return 'external_no_doctor'
  return 'nothing_shared'
}

/**
 * state → the i18n keys the card renders. The mapping lives HERE rather than in
 * the JSX so the gate can drive all six states and read the ENGLISH each one
 * selects, instead of pattern-matching a render tree.
 * ★ `with_doctor` carries no keys: the doctor's own name and specialty are the
 *   label in that state, and the status switch above it owns the tag.
 */
const DOCTOR_WAIT_LABELS = Object.freeze({
  with_doctor:         { tagKey: null,                       tagTone: 'info',    subKey: null },
  orella_pending:      { tagKey: 'caseHome.awaitingDoctor',   tagTone: 'warn',    subKey: 'caseHome.awaitingDoctorSub' },
  code_ready:          { tagKey: 'caseHome.codeReady',        tagTone: 'info',    subKey: 'caseHome.codeReadyGiveToDoctor' },
  external_code_ready: { tagKey: 'caseHome.yourOwnDoctor',    tagTone: 'info',    subKey: 'caseHome.codeReadyGiveToDoctor' },
  external_no_doctor:  { tagKey: 'caseHome.yourOwnDoctor',    tagTone: 'info',    subKey: 'caseHome.externalNotArranged' },
  nothing_shared:      { tagKey: 'caseHome.notSharedYet',     tagTone: 'default', subKey: 'caseHome.noDoctorChosen' },
})

export function doctorWaitLabels(c) {
  const state = resolveDoctorWaitState(c)
  return { state, ...DOCTOR_WAIT_LABELS[state] }
}
